import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Card } from "../../components/Card";
import { SectionTitle } from "../../components/SectionTitle";
import { LoadingContainer } from "../../components/LoadingContainer";
import { InfoBox } from "../../components/InfoBox";
import { Button } from "../../components/Button";
import colors from "@styling/colors";
import { borderRadius, spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import type { StoreId } from "@KassaroAB/shared/types/ids";
import { InventoryCostPool } from "@KassaroAB/shared/types/enums/InventoryCostPool";
import {
  useActivateInventoryCostingMutation,
  useGetInventoryCostingQuery,
} from "../../query/inventoryCostingQuery";
import { formatDateTime } from "../../utils/dateUtils";

type Method = "PURCHASE_PRICE" | "FIFO";

const Section = styled(Card)`
  max-width: 820px;
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
`;

const Muted = styled.p`
  margin: 0;
  color: ${colors.textMuted};
  font-size: ${fontSizes.medium}px;
`;

const FieldLabel = styled.span`
  font-size: ${fontSizes.medium}px;
  font-weight: 600;
`;

const Methods = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: ${spacing.x2}px;
`;

const MethodCard = styled.button<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x1}px;
  text-align: left;
  padding: ${spacing.x2}px;
  border-radius: ${borderRadius.regular}px;
  border: 2px solid
    ${({ $selected }) => ($selected ? colors.primary : colors.border)};
  background: ${colors.backgroundLight};
  color: ${colors.text};
  font: inherit;
  cursor: pointer;

  &:disabled {
    cursor: default;
  }
`;

const MethodTitle = styled.span`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing.x1}px;
  font-weight: 600;
`;

const Badge = styled.span<{ $active?: boolean }>`
  font-size: ${fontSizes.small}px;
  font-weight: 600;
  padding: 2px ${spacing.x1}px;
  border-radius: ${borderRadius.small}px;
  background: ${({ $active }) =>
    $active ? colors.successBackground : colors.backgroundDark};
  color: ${({ $active }) => ($active ? colors.successText : colors.textMuted)};
`;

const MethodText = styled.span`
  font-size: ${fontSizes.medium}px;
  color: ${colors.textMuted};
  line-height: 1.45;
`;

const FifoOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
  padding-top: ${spacing.x2}px;
  border-top: 1px solid ${colors.border};
`;

const Segment = styled.div`
  display: inline-flex;
  align-self: flex-start;
  gap: ${spacing.x05}px;
  padding: ${spacing.x05}px;
  border-radius: ${borderRadius.regular}px;
  background: ${colors.backgroundDark};
`;

const SegmentButton = styled.button<{ $selected: boolean }>`
  padding: ${spacing.x1}px ${spacing.x2}px;
  border: none;
  border-radius: ${borderRadius.small}px;
  font: inherit;
  cursor: pointer;
  background: ${({ $selected }) =>
    $selected ? colors.primary : "transparent"};
  color: ${({ $selected }) =>
    $selected ? colors.primaryContrastText : colors.text};
  font-weight: ${({ $selected }) => ($selected ? 600 : 400)};
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.x2}px;
  justify-content: flex-end;
`;

export const StockValuationSettingsView = () => {
  const { t } = useTranslation();
  const { storeId } = useParams<{ storeId: StoreId }>();
  const { data: costing, isLoading } = useGetInventoryCostingQuery(
    storeId ?? null,
  );
  const {
    mutate: activate,
    isPending: isActivating,
    error: activationError,
  } = useActivateInventoryCostingMutation(storeId ?? null);
  const [method, setMethod] = useState<Method>("PURCHASE_PRICE");
  const [pool, setPool] = useState(InventoryCostPool.LOCATION);

  const settings = costing?.settings ?? null;
  const selected: Method = settings ? "FIFO" : method;
  const selectedPool = settings?.pool ?? pool;

  return (
    <Section as="section" aria-labelledby="stock-valuation-title">
      <div>
        <SectionTitle id="stock-valuation-title">
          {t("storeSettings.valuation.title")}
        </SectionTitle>
        <Muted>{t("storeSettings.valuation.description")}</Muted>
      </div>
      {isLoading || !costing ? (
        <LoadingContainer message={t("storeSettings.loading")} />
      ) : (
        <>
          <Methods
            role="radiogroup"
            aria-label={t("storeSettings.valuation.method")}
          >
            <MethodCard
              type="button"
              role="radio"
              aria-checked={selected === "PURCHASE_PRICE"}
              $selected={selected === "PURCHASE_PRICE"}
              disabled={!!settings}
              onClick={() => setMethod("PURCHASE_PRICE")}
            >
              <MethodTitle>
                {t("storeSettings.valuation.purchasePrice")}
                {!settings && (
                  <Badge $active>{t("storeSettings.valuation.active")}</Badge>
                )}
              </MethodTitle>
              <MethodText>
                {t("storeSettings.valuation.purchasePriceDescription")}
              </MethodText>
            </MethodCard>
            <MethodCard
              type="button"
              role="radio"
              aria-checked={selected === "FIFO"}
              $selected={selected === "FIFO"}
              disabled={!!settings}
              onClick={() => setMethod("FIFO")}
            >
              <MethodTitle>
                {t("storeSettings.valuation.fifo")}
                {settings && (
                  <Badge $active>{t("storeSettings.valuation.active")}</Badge>
                )}
              </MethodTitle>
              <MethodText>
                {t("storeSettings.valuation.fifoDescription")}
              </MethodText>
            </MethodCard>
          </Methods>
          {settings ? (
            <FifoOptions>
              <Muted>
                {t("storeSettings.valuation.activeSince", {
                  date: formatDateTime(settings.activatedAt),
                  pool: t(
                    `storeSettings.valuation.poolInline.${settings.pool}`,
                  ),
                })}
              </Muted>
              <Link to={`/stores/${storeId}/stock`}>
                {t("storeSettings.valuation.showStockValue")}
              </Link>
            </FifoOptions>
          ) : selected === "FIFO" ? (
            <FifoOptions>
              <FieldLabel id="pool-label">
                {t("storeSettings.valuation.valuePer")}
              </FieldLabel>
              <Segment role="radiogroup" aria-labelledby="pool-label">
                {Object.values(InventoryCostPool).map((value) => (
                  <SegmentButton
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={selectedPool === value}
                    $selected={selectedPool === value}
                    onClick={() => setPool(value)}
                  >
                    {t(`storeSettings.valuation.pool.${value}`)}
                  </SegmentButton>
                ))}
              </Segment>
              <Muted>
                {t(`storeSettings.valuation.poolHint.${selectedPool}`)}
              </Muted>
              <InfoBox variant="warning">
                {t("storeSettings.valuation.activateWarning")}
              </InfoBox>
              {activationError ? (
                <InfoBox variant="error">{activationError.message}</InfoBox>
              ) : null}
              <Actions>
                <Button
                  type="button"
                  $variant="outlined"
                  onClick={() => setMethod("PURCHASE_PRICE")}
                >
                  {t("storeSettings.valuation.cancel")}
                </Button>
                <Button
                  type="button"
                  disabled={isActivating}
                  onClick={() => activate({ pool: selectedPool })}
                >
                  {isActivating
                    ? t("storeSettings.valuation.activating")
                    : t("storeSettings.valuation.activate")}
                </Button>
              </Actions>
            </FifoOptions>
          ) : null}
        </>
      )}
    </Section>
  );
};
